<template>
  <div class="pe-input inline-flex items-center" 
    :class="{
      'pe-input-simple-ui': simpleUi, 
      'pe-input': !simpleUi
    }"
  >
    <pe-dropdown :disabled="disabledDropDown">
      <template #default="{ active }">
        <div
          class="w-[80px] box-border flex h-[46px] items-center justify-center rounded-l-lg pl-2 pr-1 hover:cursor-pointer hover:bg-slate-200
          dark:hover:bg-neutral-700"
        >
          <img :src="country.flag" alt="Imagem do país selecionado" width="18" height="18" />
          <UiIcon
            icon="chevron-down"
            :size="18"
            class="transition-transform duration-300"
            :class="{ '-rotate-180': active }"
          />
          <span class="whitespace-nowrap text-sm">{{ country.prefix }}</span>
        </div>
      </template>
      <template #content>
        <div class="inline-flex w-48 flex-col">
          <div v-for="(item, i) of countries" :key="i" class="item" @click="country = item">
            <img :src="item.flag" alt="Imagem do país" width="18" height="18" />
            <span class="m-0 p-0">{{ item.name }}</span>
            <span class="m-0 p-0 text-slate-400">{{ item.prefix }}</span>
          </div>
        </div>
      </template>
    </pe-dropdown>
    <input
      v-model="value"
      type="text"
      v-maska
      :data-maska="JSON.stringify(country.mask || '')"
      :placeholder="placeholder ?? country.placeholder"
      :disabled="disabledInput"
      class="pe-input-content w-full grow text-inherit"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import UiIcon from '@/components/ui/icon.vue'

const props = defineProps<{
  modelValue?: string | boolean | number | null
  disabledInput?: boolean
  disabledDropDown?: boolean
  placeholder?: string
  simpleUi?: boolean
}>()

const emit = defineEmits(['update:modelValue'])

import {useI18n} from 'vue-i18n'

const { t } = useI18n()

const countries = [
  {
    code: 'BR',
    name: 'Brasil',
    prefix: '+55',
    flag: 'https://cdn.kcak11.com/CountryFlags/countries/br.svg',
    mask: ['(##) ####-####', '(##) #####-####'],
    placeholder: `(DD) + ${t('labels.number')}`
  },
  {
    code: 'PY',
    name: 'Paraguai',
    prefix: '+595',
    flag: 'https://cdn.kcak11.com/CountryFlags/countries/py.svg',
    mask: ['(##) ###-###', '(###) ###-###', '(##) #####-####', '(###) #####-####'],
    placeholder: `(DD) + ${t('labels.number')}`
  }
]

const country = ref({ ...countries[0] })

const setByCountry = (countryCode: string) => {
  const result = countries.find((el) => el.code === countryCode)
  if (result?.code) country.value = result
}

const setByPrefix = (prefix: string) => {
  country.value = countries.find((el) => el.prefix === prefix)
}

const value = computed({
  get: () => {
    const arrPre = countries.map((el) => el.prefix).sort((a, b) => b.length - a.length)

    if (props.modelValue && typeof props.modelValue === 'string') {
      const prefix = arrPre.find((pref) => pref === `${props.modelValue}`.substring(0, pref.length))
      if (prefix) {
        setByPrefix(prefix)
        return `${props.modelValue}`.split(prefix).pop()
      }
    }

    return props.modelValue
  },
  set: (val) => {
    const clean = `${val}`.replace(/\D/g, '')
    return emit('update:modelValue', `${country.value.prefix}${clean}`)
  }
})

defineExpose({
  setByCountry,
  setByPrefix
})
</script>

<style scoped lang="scss">
.pe-input {
  @apply box-border rounded-lg border-solid transition-all dark:border-neutral-600 dark:text-white border border-black;
}

.pe-input-simple-ui {
  @apply border border-[#bbb] dark:border-neutral-600 dark:bg-[#2C2C2C] dark:text-[#FDFDFD] dark:placeholder:text-[#D2D2D2];
}
.pe-input-content {
  @apply box-border border-0 bg-transparent py-[14px] pl-2 pr-4 text-sm font-normal leading-4 outline-none;
}

.item {
  @apply box-border flex w-full items-center justify-start gap-4 p-2 text-sm hover:cursor-pointer hover:bg-slate-100
  dark:hover:bg-neutral-700;
}
</style>
